import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGemeenschappen } from '../services/gemeenschappen';
import { addGuestGroup, getGuestGroups } from '../services/conviventieGuestGroupsService';
import {
  addConviventie,
  confirmLeden,
  deleteConviventie,
  getConviventies,
  updateConviventie
} from '../services/conviventieService';
import { exportConviventieBevestigingenToExcel } from '../services/export';
import { canManageConviventieTarget, normalizeUitnodigingen } from '../services/conviventieAccess';
import ConviventieCard from '../components/ConviventieCard';
import EditConviventieModal from '../components/EditConviventieModal';
import BevestigLedenPanel from '../components/BevestigLedenPanel';
import LedenUploadHandoffPanel from '../components/LedenUploadHandoffPanel';
import SectionHelpButton from '../components/SectionHelpButton';
import EmptyState from '../components/EmptyState';
import { trackConviventieAdded, trackConviventieEdited, trackConviventieDeleted, trackConviventieConfirmed, trackExportExecuted, trackGuestGroupAdded } from '../services/analytics';
import { APP_ERROR_CODES, APP_ERROR_CONTEXTS, describeAppError, formatAppErrorAlert, reportAppError, runWithTransientRetry } from '../services/appErrors';
import { deleteUploadedLeden, getUploadedLeden } from '../services/ledenUploadService';
import './ConviventiesPage.css';

const emptyForm = {
  naam: '',
  datum: '',
  eindDatum: '',
  locatie: '',
  opmerkingen: '',
  uitgenodigdeGemeenschapIds: [],
  invitedGuestGroupIds: []
};


const buildToegestaneEmails = (uitnodigingen) => {
  const emails = new Set();

  (uitnodigingen || []).forEach((item) => {
    (Array.isArray(item?.toegestaneEmails) ? item.toegestaneEmails : []).forEach((email) => {
      if (email) emails.add(email);
    });
    if (item?.email) emails.add(item.email);
    if (item?.createdBy) emails.add(item.createdBy);
  });

  return Array.from(emails);
};

const getRemovedUploadTargets = (existingConviventie, payload) => {
  const existingGemeenschappen = normalizeUitnodigingen(existingConviventie?.uitgenodigdeGemeenschappen)
    .map((item) => item?.gemeenschapId)
    .filter(Boolean);
  const nextGemeenschappen = new Set(
    normalizeUitnodigingen(payload?.uitgenodigdeGemeenschappen)
      .map((item) => item?.gemeenschapId)
      .filter(Boolean)
  );

  const existingGuestGroups = (Array.isArray(existingConviventie?.invitedGuestGroups)
    ? existingConviventie.invitedGuestGroups
    : [])
    .map((item) => item?.guestGroupId)
    .filter(Boolean);
  const nextGuestGroups = new Set(
    (Array.isArray(payload?.invitedGuestGroups) ? payload.invitedGuestGroups : [])
      .map((item) => item?.guestGroupId)
      .filter(Boolean)
  );

  return {
    removedGemeenschappen: existingGemeenschappen.filter((id) => !nextGemeenschappen.has(id)),
    removedGuestGroups: existingGuestGroups.filter((id) => !nextGuestGroups.has(id))
  };
};

const ConviventiesPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [conviventies, setConviventies] = useState([]);
  const [allGemeenschappen, setAllGemeenschappen] = useState([]);
  const [allGuestGroups, setAllGuestGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingConviventie, setEditingConviventie] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [confirmConviventieId, setConfirmConviventieId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmLedenList, setConfirmLedenList] = useState([]);
  const [ledenlijstConviventieId, setLedenlijstConviventieId] = useState(null);
  const [ledenlijstTarget, setLedenlijstTarget] = useState(null);

  // Guards the confirmation submit against double clicks (Feature 40); the ref is
  // what makes it race-proof, the state only drives the disabled button.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [convivRows, gemeenschapRows] = await Promise.all([
        getConviventies(),
        getGemeenschappen()
      ]);

      const guestGroupsRows = isAdmin ? await getGuestGroups() : [];

      setConviventies(convivRows);
      setAllGemeenschappen(gemeenschapRows);
      setAllGuestGroups(guestGroupsRows);
    } catch (error) {
      console.error('Error loading conviventies:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.CONVIVENTIES_LOAD);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const gemeenschapById = useMemo(() => {
    const map = new Map();
    allGemeenschappen.forEach((item) => map.set(item.id, item));
    return map;
  }, [allGemeenschappen]);

  const openCreate = () => {
    setEditingConviventie(null);
    setFormData(emptyForm);
    setIsEditModalOpen(true);
  };

  const openEdit = (conviventie) => {
    const uitnodigingen = normalizeUitnodigingen(conviventie.uitgenodigdeGemeenschappen);
    setEditingConviventie(conviventie);
    setFormData({
      naam: conviventie.naam || '',
      datum: conviventie.datum || '',
      eindDatum: conviventie.eindDatum || '',
      locatie: conviventie.locatie || '',
      opmerkingen: conviventie.opmerkingen || '',
      uitgenodigdeGemeenschapIds: uitnodigingen.map((g) => g.gemeenschapId),
      invitedGuestGroupIds: (Array.isArray(conviventie.invitedGuestGroups) ? conviventie.invitedGuestGroups : [])
        .map((g) => g.guestGroupId)
        .filter(Boolean)
    });
    setIsEditModalOpen(true);
  };

  const closeEdit = () => {
    setIsEditModalOpen(false);
    setEditingConviventie(null);
    setFormData(emptyForm);
  };

  const toggleGemeenschap = (id) => {
    const current = formData.uitgenodigdeGemeenschapIds;
    if (current.includes(id)) {
      setFormData({
        ...formData,
        uitgenodigdeGemeenschapIds: current.filter((value) => value !== id)
      });
    } else {
      setFormData({
        ...formData,
        uitgenodigdeGemeenschapIds: [...current, id]
      });
    }
  };

  const toggleGuestGroup = (id) => {
    const current = formData.invitedGuestGroupIds || [];
    if (current.includes(id)) {
      setFormData({
        ...formData,
        invitedGuestGroupIds: current.filter((value) => value !== id)
      });
    } else {
      setFormData({
        ...formData,
        invitedGuestGroupIds: [...current, id]
      });
    }
  };

  const createAndSelectGuestGroup = async () => {
    const derivedName = `${formData.naam.trim() || 'Nieuwe conviventie'} - gasten`;

    const existing = allGuestGroups.find(
      (group) => (group.name || '').trim().toLowerCase() === derivedName.toLowerCase()
    );

    if (existing?.id) {
      setFormData((prev) => ({
        ...prev,
        invitedGuestGroupIds: Array.from(new Set([...(prev.invitedGuestGroupIds || []), existing.id]))
      }));
      return;
    }

    try {
      const created = await addGuestGroup({ name: derivedName });
      trackGuestGroupAdded();

      setAllGuestGroups((prev) => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setFormData((prev) => ({
        ...prev,
        invitedGuestGroupIds: Array.from(new Set([...(prev.invitedGuestGroupIds || []), created.id]))
      }));
    } catch (error) {
      console.error('Error creating guest group:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.GUEST_GROUP_CREATE);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    }
  };

  const toUitgenodigdeGemeenschappen = () => {
    const existingUitnodigingen = normalizeUitnodigingen(editingConviventie?.uitgenodigdeGemeenschappen);
    const existingById = new Map(
      existingUitnodigingen.map((item) => [item.gemeenschapId, item])
    );

    return formData.uitgenodigdeGemeenschapIds.map((id) => {
      const source = gemeenschapById.get(id) || {};
      const existing = existingById.get(id);

      return {
        gemeenschapId: id,
        gemeenschapNaam: source.gemeenschap || existing?.gemeenschapNaam || '',
        bisdom: source.bisdom || existing?.bisdom || '',
        parochie: source.parochie || existing?.parochie || '',
        email: source.email || existing?.email || '',
        createdBy: source.createdBy || existing?.createdBy || '',
        toegestaneEmails: source.toegestaneEmails || existing?.toegestaneEmails || [],
        uploadStatus: existing?.uploadStatus || 'pending',
        uploadedAt: existing?.uploadedAt || null,
        uploadedBy: existing?.uploadedBy || '',
        bevestigdeLeden: existing?.bevestigdeLeden || [],
        updatedAt: existing?.updatedAt || null,
        updatedBy: existing?.updatedBy || ''
      };
    });
  };

  const toInvitedGuestGroups = (groupIds, createdGroups = []) => {
    const existingInvitedGuestGroups = Array.isArray(editingConviventie?.invitedGuestGroups)
      ? editingConviventie.invitedGuestGroups
      : [];
    const existingById = new Map(existingInvitedGuestGroups.map((item) => [item.guestGroupId, item]));
    const allKnownGroups = [...allGuestGroups, ...createdGroups];
    const knownById = new Map(allKnownGroups.map((item) => [item.id, item]));

    return groupIds.map((groupId) => {
      const source = knownById.get(groupId);
      const existing = existingById.get(groupId);

      return {
        guestGroupId: groupId,
        guestGroupName: source?.name || existing?.guestGroupName || '',
        uploadStatus: existing?.uploadStatus || 'pending',
        uploadedAt: existing?.uploadedAt || null,
        uploadedBy: existing?.uploadedBy || '',
        bevestigdeLeden: existing?.bevestigdeLeden || []
      };
    });
  };

  const submitConviventie = async () => {
    if (!formData.naam.trim() || !formData.datum || !formData.eindDatum) {
      alert('Naam, start datum en eind datum zijn verplicht');
      return;
    }

    if (formData.eindDatum < formData.datum) {
      alert('Eind datum kan niet voor start datum liggen');
      return;
    }

    const uitgenodigdeGemeenschappen = toUitgenodigdeGemeenschappen();
    const guestGroupIds = Array.from(new Set(formData.invitedGuestGroupIds || []));

    const payload = {
      naam: formData.naam.trim(),
      datum: formData.datum,
      eindDatum: formData.eindDatum,
      locatie: formData.locatie.trim(),
      opmerkingen: formData.opmerkingen.trim(),
      uitgenodigdeGemeenschappen,
      invitedGuestGroups: toInvitedGuestGroups(guestGroupIds),
      toegestaneEmails: buildToegestaneEmails(uitgenodigdeGemeenschappen)
    };

    try {
      if (editingConviventie) {
        await updateConviventie(editingConviventie.id, payload);

        const { removedGemeenschappen, removedGuestGroups } = getRemovedUploadTargets(editingConviventie, payload);
        const cleanupResults = await Promise.allSettled([
          ...removedGemeenschappen.map((id) => deleteUploadedLeden(editingConviventie.id, id, 'gemeenschap')),
          ...removedGuestGroups.map((id) => deleteUploadedLeden(editingConviventie.id, id, 'guestGroup'))
        ]);

        const failedCleanupCount = cleanupResults.filter((result) => result.status === 'rejected').length;
        if (failedCleanupCount > 0) {
          console.error('Failed to cleanup some de-invited leden uploads:', failedCleanupCount);
          alert('Conviventie opgeslagen, maar niet alle oude uploadbestanden konden verwijderd worden. Probeer opnieuw.');
        }

        trackConviventieEdited({
          locatie: payload.locatie,
          invited_groups_count: payload.uitgenodigdeGemeenschappen.length
        });
      } else {
        await addConviventie(payload);
        trackConviventieAdded({
          locatie: payload.locatie,
          invited_groups_count: payload.uitgenodigdeGemeenschappen.length
        });
      }
      closeEdit();
      await loadData();
    } catch (error) {
      console.error('Error saving conviventie:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.CONVIVENTIE_SAVE);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    }
  };

  const removeConviventie = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze conviventie wilt verwijderen?')) {
      return;
    }

    try {
      const deletedConviventie = conviventies.find((c) => c.id === id);
      await deleteConviventie(id);
      trackConviventieDeleted({ locatie: deletedConviventie?.locatie });
      await loadData();
    } catch (error) {
      console.error('Error deleting conviventie:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.CONVIVENTIE_DELETE);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    }
  };

  const canManageGemeenschapUpload = useCallback((gemeenschap) => (
    canManageConviventieTarget(gemeenschap, gemeenschapById.get(gemeenschap.gemeenschapId), {
      isAdmin,
      userEmail: user?.email
    })
  ), [gemeenschapById, isAdmin, user]);

  const canConfirmGemeenschap = useCallback((gemeenschap) => {
    return canManageGemeenschapUpload(gemeenschap);
  }, [canManageGemeenschapUpload]);

  const visibleConviventies = useMemo(() => {
    if (isAdmin) return conviventies;

    return conviventies.filter((conviventie) => {
      const uitnodigingen = normalizeUitnodigingen(conviventie.uitgenodigdeGemeenschappen);
      return uitnodigingen.some((gemeenschap) => canConfirmGemeenschap(gemeenschap));
    });
  }, [canConfirmGemeenschap, conviventies, isAdmin]);

  const resetConfirmPanel = () => {
    setConfirmConviventieId(null);
    setConfirmTarget(null);
    setConfirmLedenList([]);
  };

  const resetLedenlijstPanel = () => {
    setLedenlijstConviventieId(null);
    setLedenlijstTarget(null);
  };

  const canConfirmGuestGroup = useCallback(() => isAdmin, [isAdmin]);

  // Feature 38: which row action is "pressed" — the one whose panel (confirm or
  // upload handoff) is currently open. Only one panel can be open at a time.
  const activePanelTarget = useMemo(() => {
    if (confirmConviventieId && confirmTarget) {
      return { conviventieId: confirmConviventieId, ...confirmTarget };
    }
    if (ledenlijstConviventieId && ledenlijstTarget) {
      return { conviventieId: ledenlijstConviventieId, ...ledenlijstTarget };
    }
    return null;
  }, [confirmConviventieId, confirmTarget, ledenlijstConviventieId, ledenlijstTarget]);


  const toggleLedenlijstPanel = (conviventieId, target, targetType = 'gemeenschap') => {
    if (!conviventieId) return;

    const targetId = targetType === 'guestGroup' ? target.guestGroupId : target.gemeenschapId;
    const isSameOpen =
      ledenlijstConviventieId === conviventieId &&
      ledenlijstTarget?.targetType === targetType &&
      ledenlijstTarget?.targetId === targetId;

    if (isSameOpen) {
      resetLedenlijstPanel();
      return;
    }

    setLedenlijstConviventieId(conviventieId);
    setLedenlijstTarget({
      targetType,
      targetId,
      label: targetType === 'guestGroup' ? target.guestGroupName : target.gemeenschapNaam
    });
  };

  // The upload UI lives only on the Leden page, so hand the selected target over
  // through the URL and let that page preselect it.
  const goToLedenPage = (conviventieId, target) => {
    const params = new URLSearchParams({
      conviventie: conviventieId,
      targetType: target.targetType,
      targetId: target.targetId
    });

    resetLedenlijstPanel();
    navigate(`/leden?${params.toString()}`);
  };

  const openConfirmPanel = async (conviventieId, target, targetType = 'gemeenschap') => {
    if (!conviventieId) return;

    const isSameOpen =
      confirmConviventieId === conviventieId &&
      confirmTarget?.targetType === targetType &&
      confirmTarget?.targetId === (targetType === 'guestGroup' ? target.guestGroupId : target.gemeenschapId);

    if (isSameOpen) {
      resetConfirmPanel();
      return;
    }

    try {
      const isGuestGroup = targetType === 'guestGroup';
      const targetId = isGuestGroup ? target.guestGroupId : target.gemeenschapId;
      const uploaded = await getUploadedLeden(conviventieId, targetId, targetType);
      const leden = uploaded.leden || [];

      setConfirmConviventieId(conviventieId);
      setConfirmLedenList(leden);
      setConfirmTarget({
        targetType,
        targetId,
        label: isGuestGroup ? target.guestGroupName : target.gemeenschapNaam,
        bevestigdeLeden: target.bevestigdeLeden || [],
        uploadedAt: uploaded.uploadedAt || target.uploadedAt || null,
        uploadedBy: uploaded.uploadedBy || target.uploadedBy || ''
      });
    } catch (error) {
      console.error('Error loading leden for confirmation:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.LEDEN_LOAD);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    }
  };

  const submitConfirmation = async (selectedLeden) => {
    if (!confirmConviventieId || !confirmTarget) return;

    // Double-submit guard (Feature 40): confirmLeden rewrites the whole invite
    // array, so two in-flight confirmations are last-write-wins. The ref is
    // checked before the state re-render, which makes it race-proof.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    const conviventieId = confirmConviventieId;
    const target = confirmTarget;

    try {
      // Transient connectivity failures (offline, deadline exceeded) are retried
      // twice before the user ever sees an error.
      await runWithTransientRetry(
        () => confirmLeden(conviventieId, target.targetId, selectedLeden, target.targetType),
        { attempts: 3 }
      );
      trackConviventieConfirmed({
        confirmed_count: selectedLeden.reduce((sum, l) => sum + (l.aantal || 1), 0),
        has_notes_count: selectedLeden.filter((l) => l.bijzonderheden).length
      });
      resetConfirmPanel();

      await loadData();
    } catch (error) {
      console.error('Error confirming leden:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.CONFIRM_LEDEN);
      reportAppError(info, {
        error,
        meta: {
          conviventieId,
          targetType: target?.targetType,
          targetId: target?.targetId
        }
      });

      // A stale target or a deleted conviventie cannot be fixed by retrying:
      // refresh the list so the dead panel/card disappears.
      const isStaleTarget =
        info.code === APP_ERROR_CODES.CONVIVENTIE_MISSING ||
        info.code === APP_ERROR_CODES.TARGET_MISSING;

      if (isStaleTarget) {
        resetConfirmPanel();
        await loadData();
      }

      alert(formatAppErrorAlert(info));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleExport = (conviventie) => {
    const date = new Date().toISOString().split('T')[0];
    const safeName = (conviventie.naam || 'conviventie').replace(/\s+/g, '-').toLowerCase();
    const safeDatum = conviventie.datum || 'zonder-datum';
    const filename = `${safeName}-${safeDatum}-export-${date}`;
    exportConviventieBevestigingenToExcel(conviventie, filename);
    trackExportExecuted({ export_type: 'conviventies', item_count: 1 });
  };

  if (loading) {
    return (
      <div className="conviventies-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  return (
    <div className="conviventies-page">
      <div className="app-page-header conviventies-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Conviventies</h2>
            <SectionHelpButton
              title="Conviventies"
              purpose="Hier plant u conviventies en volgt u op welke gemeenschappen en leden bevestigd zijn."
              instructions={[
                'Voor admins: maak nieuwe conviventies aan, bewerk of verwijder bestaande conviventies en nodig gemeenschappen uit.',
                'Voor verantwoordelijken: gebruik Bevestig deelname om aanwezigheid van leden voor het evenement te bevestigen. Note: alleen voor gemeenschappen waarvoor u zelf verantwoordelijk bent.',
                'Voor admins: gebruik Exporteren om bevestigingen te delen of verder te verwerken met het reserveren van hotels kamers.'
              ]}
            />
          </div>
          <p className="app-page-description">Plan conviventies, nodig gemeenschappen uit en volg bevestigingen op.</p>
        </div>
        {isAdmin && (
          <button className="add-button" onClick={openCreate}>
            + Nieuwe Conviventie
          </button>
        )}
      </div>

      {visibleConviventies.length === 0 ? (
        <EmptyState
          icon="📅"
          message="Geen toekomstige conviventies gevonden"
          actionLabel={isAdmin ? '+ Nieuwe Conviventie' : undefined}
          onAction={isAdmin ? openCreate : undefined}
        />
      ) : (
        <div className="conviventies-grid">
          {visibleConviventies.map((conviventie) => (
            <div key={conviventie.id} className="conviventie-entry">
              <ConviventieCard
                conviventie={conviventie}
                canExport={isAdmin}
                onExport={handleExport}
                canConfirmGemeenschap={canConfirmGemeenschap}
                canManageGemeenschapUpload={canManageGemeenschapUpload}
                onConfirmGemeenschap={(gemeenschap) => openConfirmPanel(conviventie.id, gemeenschap)}
                onManageGemeenschapUpload={(gemeenschap) => toggleLedenlijstPanel(conviventie.id, gemeenschap)}
                canConfirmGuestGroup={canConfirmGuestGroup}
                onConfirmGuestGroup={(guestGroup) => openConfirmPanel(conviventie.id, guestGroup, 'guestGroup')}
                onManageGuestGroupUpload={(guestGroup) => toggleLedenlijstPanel(conviventie.id, guestGroup, 'guestGroup')}
                activePanelTarget={
                  activePanelTarget?.conviventieId === conviventie.id ? activePanelTarget : null
                }
                canEdit={isAdmin}
                onEdit={openEdit}
                onDelete={removeConviventie}
              >
                <LedenUploadHandoffPanel
                  isOpen={
                    ledenlijstConviventieId === conviventie.id &&
                    !!ledenlijstTarget
                  }
                  targetLabel={ledenlijstTarget?.label || ''}
                  onOpenLedenlijst={() => goToLedenPage(conviventie.id, ledenlijstTarget)}
                  onClose={resetLedenlijstPanel}
                />
                <BevestigLedenPanel
                  isOpen={
                    confirmConviventieId === conviventie.id &&
                    !!confirmTarget
                  }
                  leden={confirmLedenList}
                  initialConfirmedLeden={confirmTarget?.bevestigdeLeden || []}
                  gemeenschapNaam={confirmTarget?.label || ''}
                  isGuestGroup={confirmTarget?.targetType === 'guestGroup'}
                  conviventieNaam={conviventie.naam || ''}
                  conviventieStartDate={conviventie.datum || ''}
                  conviventieEndDate={conviventie.eindDatum || ''}
                  uploadedAt={confirmTarget?.uploadedAt || null}
                  uploadedBy={confirmTarget?.uploadedBy || ''}
                  onManageLeden={() => goToLedenPage(conviventie.id, confirmTarget)}
                  submitting={isSubmitting}
                  onSubmit={submitConfirmation}
                  onClose={resetConfirmPanel}
                />
              </ConviventieCard>
            </div>
          ))}
        </div>
      )}

      <EditConviventieModal
        isOpen={isEditModalOpen}
        isEditMode={!!editingConviventie}
        formData={formData}
        onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
        onToggleGemeenschap={toggleGemeenschap}
        onToggleGuestGroup={toggleGuestGroup}
        onCreateGuestGroup={createAndSelectGuestGroup}
        availableGemeenschappen={allGemeenschappen}
        availableGuestGroups={allGuestGroups}
        onSubmit={submitConviventie}
        onClose={closeEdit}
      />

    </div>
  );
};

export default ConviventiesPage;
